import { LoadingOutlined, RedoOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { Image as AntdImage, Dropdown, ImageProps, Menu } from 'antd';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import useStatus from 'hooks/useStatus';
import { isNil, isUndefined } from 'lodash-es';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import wait from 'utils/wait';
import { ReactComponent as DefaultPlaceholderSvg } from './placeholder.svg';
import { useSortedShopeeDomains, useUpdateShopeeDomainScore } from './useShopeeDomainScore';
import {
  getImageThumb,
  getTiktokProxyImageSrc,
  isBase64,
  isLazadaPic,
  isShopeePic,
  lazadaPicCompress,
} from 'lib/utils/platformImage';
import { useInViewport, useMemoizedFn, useTrackedEffect } from 'ahooks';
import { DialogueModeContext } from 'views/Conversation/Chat/components/DialogueModeContext.tsx';
import copyText from 'utils/copyText';
import { useTranslate } from '@tolgee/react';
import useProxySrc from 'hooks/business/useProxySrc';

const Reload = styled(RedoOutlined)`
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: 18px;
  transform: translate(-50%, -50%);
`;

const Span = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--fett-erp-card-bg);
`;

const ErrorSpan = styled(Span)`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.3);
    opacity: 0;
    pointer-events: none;
  }
  ${Reload} {
    color: #fff;
    display: none;
    cursor: pointer;
  }
  &:hover {
    ${Reload} {
      display: block;
    }
    &::before {
      opacity: 1;
    }
  }
`;

export const replaceShopeeHost = (src: string, domain: string) => {
  if (domain === 'origin' || !domain) {
    return src;
  }

  const url = new URL(src);

  url.host = domain;

  return url.href;
};

export const loadImage = (src: string, signal?: AbortSignal) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const cleanup = () => {
      image.removeAttribute('src');
      image.removeEventListener('load', onLoad);
      image.removeEventListener('error', onError);
      image.removeEventListener('abort', onAbort);
      signal?.removeEventListener('abort', onAbort);
    };
    const onLoad = (e: Event) => {
      cleanup();
      resolve(e);
    };
    const onError = () => {
      cleanup();
      reject(new Error('Load image error'));
    };
    const onAbort = () => {
      cleanup();
      reject(new DOMException('Load image aborted', 'AbortError'));
    };
    image.addEventListener('load', onLoad);
    image.addEventListener('error', onError);
    image.addEventListener('abort', onAbort);
    signal?.addEventListener('abort', onAbort);
    image.src = src;
  });
};

const timeoutTime = 20 * 1000;

const loadImageWithReport = (
  src: string,
  score: number,
  reportAll = false,
  signal?: AbortSignal,
) => {
  const start = Date.now();

  const report = (time: number, error?: boolean) => {
    const { host } = new URL(src);

    const id = src.split('/').pop();

    const timeout = time > timeoutTime;

    posthog.capture('$shopeeImageLoad', {
      host,
      id,
      time,
      timeout,
      score,
      error,
    });
  };

  return loadImage(src, signal)
    .then(() => {
      const end = Date.now();

      const time = end - start;

      if (reportAll || time > 5 * 1000) {
        report(time);
      }

      return time;
    })
    .catch((err) => {
      const end = Date.now();

      const time = end - start;

      if (navigator.onLine) {
        report(time, true);
      }

      return Promise.reject(err);
    });
};

const loadImageWithTimeout = (
  src: string,
  score: number,
  reportAll = false,
  signal?: AbortSignal,
) => {
  return new Promise<number>((resolve, reject) => {
    loadImageWithReport(src, score, reportAll, signal).then(resolve).catch(reject);

    wait(timeoutTime).then(reject);
  });
};

export interface SafeImageProps extends Omit<ImageProps, 'placeholder' | 'preview'> {
  placeholder?: React.ReactNode;
  spin?: React.ReactNode;
  preview?: boolean;
  reload?: boolean;
  // 是否有缩略图，以平台划分，如果2个平台都有，侧传入'both'
  hasSmallPic?: General.Platform | 'both';
  imgSize?: {
    width: number;
    height: number;
  };
  platform?: General.CaselessPlatformTunnel;
}

function SafeImage(props: SafeImageProps & { sizedStyle: { width: string; height: string } }) {
  const {
    src: srcProp,
    placeholder,
    spin,
    className,
    preview: previewProp = false,
    reload = true,
    hasSmallPic,
    imgSize,
    platform,
    sizedStyle,
    ...otherProps
  } = props;

  const abortControllerRef = useRef(new AbortController());
  const isMountedRef = useRef(true);
  const { pending, error, done } = useStatus({ defaultPending: true });

  const [refreshKey, setRefreshKey] = useState(0);

  const [src, setSrc] = useState(srcProp);

  const [thumb, setThumb] = useState(srcProp ? getImageThumb(srcProp, platform) : srcProp);

  const sortedShopeeDomains = useSortedShopeeDomains();

  const updateShopeeDomainScore = useUpdateShopeeDomainScore();

  const { shopeeImageReportAllEnable } = useOrgConfigStateValue();

  const { getProxyEnable, errorLog } = useProxySrc();

  const refsCurrent = {
    done,
    sortedShopeeDomains,
    updateShopeeDomainScore,
    shopeeImageReportAllEnable,
    isMountedRef,
    errorHandler(err: Error) {
      if (!isMountedRef.current) return;
      done.error(err);
      posthog.capture('$loadSafeImageError', {
        src,
        platform,
        errMsg: err?.message,
      });
    },
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const loadShopeeImage = useMemoizedFn((srcProp: string, shouldThumbChange: boolean) => {
    const {
      current: {
        done,
        sortedShopeeDomains,
        updateShopeeDomainScore,
        shopeeImageReportAllEnable,
        errorHandler,
      },
    } = refs;

    const { signal } = abortControllerRef.current;

    sortedShopeeDomains
      .reduce<Promise<any>>((res, cur) => {
        return res
          .then(() => res)
          .catch(() => {
            const { domain, score } = cur;

            const src = replaceShopeeHost(srcProp, domain);

            const thumb = getImageThumb(src, 'SHOPEE');
            const load = (src: string, thumb: string) =>
              loadImageWithTimeout(thumb, score, shopeeImageReportAllEnable, signal)
                .then((time) => {
                  if (!isMountedRef.current) return time;
                  setSrc(src);
                  shouldThumbChange && setThumb(thumb);
                  updateShopeeDomainScore(domain, time > 5 * 1000 ? -5 : 1);
                  return time;
                })
                .catch((e: DOMException) => {
                  if (e.name !== 'AbortError') {
                    return loadImageWithTimeout(src, score, shopeeImageReportAllEnable, signal);
                  }
                  throw e;
                })
                .then((time) => {
                  if (!isMountedRef.current) return;
                  setSrc(src);
                  shouldThumbChange && setThumb(thumb);
                  updateShopeeDomainScore(domain, time > 5 * 1000 ? -5 : 1);
                })
                .catch((err: Error) => {
                  errorLog(src);
                  if (!isMountedRef.current) return Promise.reject(err);
                  if (navigator.onLine && err.name !== 'AbortError') {
                    updateShopeeDomainScore(domain, -20);
                  }

                  return Promise.reject(err);
                });
            if (getProxyEnable(src)) {
              const proxySrc = getTiktokProxyImageSrc(src);
              const proxyThumb = getTiktokProxyImageSrc(thumb);
              return load(proxySrc, proxyThumb).catch(() => load(src, thumb));
            } else {
              load(src, thumb);
            }
          });
      }, Promise.reject())
      .then(() => {
        if (isMountedRef.current) {
          done();
        }
      })
      .catch(errorHandler);
  });

  const loadLazadaImage = useMemoizedFn((srcProp: string) => {
    const {
      current: { done, errorHandler },
    } = refs;
    const { signal } = abortControllerRef.current;
    const thumb = lazadaPicCompress(srcProp, 128);

    const onLoad = (src: string) => {
      if (!isMountedRef.current) return;
      setThumb(src);
      done();
    };

    const load = () =>
      loadImage(thumb, signal)
        .then(() => {
          onLoad(thumb);
        })
        .catch(() => {
          errorLog(thumb);
          loadImage(srcProp, signal)
            .then(() => {
              onLoad(srcProp);
            })
            .catch((error) => {
              errorLog(srcProp);
              return errorHandler(error);
            });
        });

    if (getProxyEnable(thumb)) {
      const proxySrc = getTiktokProxyImageSrc(thumb);
      loadImage(proxySrc, signal)
        .then(() => {
          onLoad(proxySrc);
          setSrc(getTiktokProxyImageSrc(srcProp));
        })
        .catch(() => {
          errorLog(proxySrc);
          return load();
        });
    } else {
      load();
    }
  });

  const loadPlatformImage = useMemoizedFn((src: string) => {
    const {
      current: { done, errorHandler },
    } = refs;
    const { signal } = abortControllerRef.current;
    const onLoad = (src: string) => {
      if (!isMountedRef.current) return;
      setThumb(src);
      done();
    };
    const load = () =>
      loadImage(src, signal)
        .then(() => {
          onLoad(src);
        })
        .catch((error) => {
          errorLog(src);
          return errorHandler(error);
        });
    if (getProxyEnable(src)) {
      const proxySrc = getTiktokProxyImageSrc(src);
      loadImage(proxySrc, signal)
        .then(() => {
          onLoad(proxySrc);
          setSrc(proxySrc);
        })
        .catch(() => {
          errorLog(proxySrc);
          return load();
        });
    } else {
      load();
    }
  });

  useTrackedEffect(
    (_, preDeps, curDeps) => {
      // 取消之前的请求
      abortControllerRef.current.abort();
      // 创建新的 AbortController
      abortControllerRef.current = new AbortController();

      const [preSrcProp, preRefreshKey] = (preDeps as any) || [];
      const [curSrcProp, curRefreshKey] = (curDeps as any) || [];

      const shouldThumbChange =
        preRefreshKey !== curRefreshKey ||
        (preSrcProp &&
          curSrcProp &&
          !preSrcProp.startsWith('blob:') &&
          !curSrcProp.startsWith('blob:'));

      const {
        current: { done },
      } = refs;

      if (!srcProp || isBase64(srcProp)) {
        if (isMountedRef.current) {
          done();
        }
        return;
      }

      if (isShopeePic(srcProp)) {
        loadShopeeImage(srcProp, shouldThumbChange);
        return;
      }

      if (isMountedRef.current) {
        setSrc(srcProp);
      }

      if (!shouldThumbChange) {
        return;
      }

      if (isLazadaPic(srcProp)) {
        loadLazadaImage(srcProp);
        return;
      }

      loadPlatformImage(srcProp);
    },
    [srcProp, refreshKey],
  );

  useEffect(() => {
    isMountedRef.current = true;
    const abortController = abortControllerRef.current;
    return () => {
      isMountedRef.current = false;
      abortController.abort();
    };
  }, []);

  if (pending) {
    return isUndefined(spin) ? (
      <Span style={sizedStyle} className={cx(className, 'ant-image')}>
        <LoadingOutlined />
      </Span>
    ) : (
      <>{spin}</>
    );
  }

  if (error || !srcProp) {
    return (
      <ErrorSpan
        style={sizedStyle}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          e.nativeEvent.stopPropagation();
          e.nativeEvent.preventDefault();
          setRefreshKey((_) => _ + 1);
        }}
        className={cx(className, 'ant-image')}
      >
        {reload ? <Reload /> : null}
        {placeholder ?? (
          <>
            {/* @ts-ignore */}
            <DefaultPlaceholderSvg {...otherProps} />
          </>
        )}
      </ErrorSpan>
    );
  }

  return (
    <AntdImage
      src={isBase64(srcProp) ? srcProp : thumb}
      alt=""
      style={sizedStyle}
      className={className}
      preview={previewProp ? { src } : previewProp}
      {...otherProps}
    />
  );
}

const SafeImageWrapper = (
  props: SafeImageProps & { target?: Element | null; onLoad?: () => void },
) => {
  const { src, target, imgSize, width, height, onLoad, ...others } = props;
  const { t } = useTranslate();
  const ref = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState(isNil(target));
  const isFaq = useContext(DialogueModeContext) === 'faq';
  const [inViewPort] = useInViewport(ref, {
    root: target ?? undefined,
    rootMargin: '100px',
  });

  const { resolveSrc } = useProxySrc();

  const style = useMemo(() => {
    let wpx = '';
    let hpx = '';
    let minHeight = '';
    if (width || height) {
      wpx = typeof width === 'number' ? `${width}px` : width || '';
      hpx = typeof height === 'number' ? `${height}px` : height || '';
      if (!width) {
        wpx = 'auto';
      }
      minHeight = wpx || '';
      if (!height) {
        hpx = 'auto';
      } else {
        minHeight = hpx;
      }
    }
    return { width: wpx, height: hpx, minHeight };
  }, [height, width]);

  const sizedStyle = useMemo(() => {
    if (!imgSize) {
      return style;
    }
    const w = parseInt(width ? String(width) : '200', 10);
    const radio = (imgSize.height / imgSize.width).toFixed(3);
    const h = (w * Number(radio)).toFixed(2);
    return {
      width: `${w}px`,
      height: `${h}px`,
    };
  }, [width, imgSize, style]);

  const memoOnLoad = useMemoizedFn(() => onLoad?.());

  useEffect(() => {
    if ((inViewPort || isFaq) && !load) {
      setLoad(true);
      memoOnLoad();
    }
  }, [inViewPort, isFaq, load, memoOnLoad]);

  const img = <SafeImage sizedStyle={sizedStyle} src={src} {...others} />;

  return (
    <div ref={ref} className="w-fit h-fit" style={sizedStyle}>
      {load &&
        (src ? (
          <Dropdown
            trigger={['contextMenu']}
            overlay={
              <Menu
                onClick={({ domEvent }) => {
                  domEvent.stopPropagation();
                  // 与展示使用同一套代理规则，避免复制出无法访问的裸地址
                  copyText(resolveSrc(src));
                }}
              >
                <Menu.Item>{t('authShop.复制链接')}</Menu.Item>
              </Menu>
            }
          >
            <span>{img}</span>
          </Dropdown>
        ) : (
          img
        ))}
    </div>
  );
};

export default SafeImageWrapper;
